import { services } from '@config/services';
import type { ExecutionLog } from '../types';
import { integrationApi } from './_base';

/**
 * Execution-logs endpoint. Note: this endpoint lives on the
 * *transformation* microservice (not integration) and is scoped to
 * plugin rows via `hasPluginContext=true`. Merchant-scope is applied
 * server-side from the auth headers; merchantId is passed only for the
 * super-admin "viewing as merchant" case.
 */

export interface ExecutionLogArgs {
  merchantId?: string;
  searchString?: string;
  pluginType?: string;
  status?: 'Success' | 'Failed' | 'Timeout' | '';
  /** Whole event name; the backend's `eventName` param is an infix match, so dropdown picks use this one. */
  eventNameExact?: string;
  direction?: 'Inbound' | 'Outbound' | '';
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortAsc?: boolean;
}

/**
 * Values for the Transaction / Direction dropdowns. `eventNames` are the distinct
 * event names present in the scoped merchant's own logs, deliberately not the
 * platform Events Catalog: logged names such as `shipment-updated-outbound` are
 * not catalog entries, and a catalog list would offer events with no logs.
 */
export interface ExecutionLogFilterOptions {
  eventNames: string[];
  directions: string[];
}

export type RawExecutionLogsResponse =
  | ExecutionLog[]
  | { executionLogs?: ExecutionLog[]; total?: number };

export interface ExecutionLogsResult {
  logs: ExecutionLog[];
  total: number;
}

/** The filter-options body as it comes off the wire, before it is trusted. */
interface RawExecutionLogFilterOptions {
  eventNames?: unknown[];
  directions?: string[];
}

export function transformFilterOptionsResponse(
  response: RawExecutionLogFilterOptions | undefined,
): ExecutionLogFilterOptions {
  // Event names become Select item values, which must be unique non-empty strings.
  const eventNames = [
    ...new Set(
      (response?.eventNames ?? []).filter(
        (name): name is string => typeof name === 'string' && name.trim() !== '',
      ),
    ),
  ];
  return { eventNames, directions: response?.directions ?? [] };
}

export function transformExecutionLogsResponse(
  response: RawExecutionLogsResponse,
): ExecutionLogsResult {
  if (Array.isArray(response)) return { logs: response, total: response.length };
  const logs = response.executionLogs ?? [];
  return { logs, total: response.total ?? logs.length };
}

export function buildExecutionLogQuery(args: ExecutionLogArgs): string {
  const p = new URLSearchParams();
  p.set('hasPluginContext', 'true');
  if (args.searchString) p.set('q', args.searchString);
  if (args.pluginType) p.set('pluginType', args.pluginType);
  if (args.status) p.set('status', args.status);
  if (args.eventNameExact) p.set('eventNameExact', args.eventNameExact);
  if (args.direction) p.set('direction', args.direction);
  if (args.page != null) p.set('page', String(args.page));
  if (args.pageSize != null) p.set('pageSize', String(args.pageSize));
  if (args.sortBy) p.set('sortBy', args.sortBy);
  if (args.sortAsc != null) p.set('sortAsc', String(args.sortAsc));
  if (args.merchantId) p.set('merchantId', args.merchantId);
  return p.toString();
}

const executionLogsApi = integrationApi.injectEndpoints({
  endpoints: (builder) => ({
    getExecutionLogs: builder.query<ExecutionLogsResult, ExecutionLogArgs | void>({
      query: (args) => ({
        url: `${services.transformation}/executionLogs?${buildExecutionLogQuery(args ?? {})}`,
        method: 'GET',
      }),
      transformResponse: transformExecutionLogsResponse,
      providesTags: () => [{ type: 'ExecutionLogs', id: 'LIST' }],
    }),
    getExecutionLogFilterOptions: builder.query<
      ExecutionLogFilterOptions,
      { merchantId?: string; scopeKey?: string } | void
    >({
      // Live wire:
      //   GET /transformation/executionLogs/filterOptions[?merchantId=<uuid>]
      //   -> { eventNames: string[], directions: string[] }
      // `scopeKey` is not sent: it only separates cache entries for scopes that share
      // a `merchantId` (admin and vendor scope are both undefined) but see different options.
      query: (args) => ({
        url: `${services.transformation}/executionLogs/filterOptions${
          args?.merchantId ? `?merchantId=${encodeURIComponent(args.merchantId)}` : ''
        }`,
        method: 'GET',
      }),
      transformResponse: transformFilterOptionsResponse,
      providesTags: () => [{ type: 'ExecutionLogs', id: 'FILTER_OPTIONS' }],
      // Slow-changing reference data (the event names a scope has logged), so keep it for
      // 5 minutes after the last subscriber leaves instead of refetching on every tab switch.
      // Refresh on the tab refetches it explicitly.
      keepUnusedDataFor: 300,
    }),
  }),
});

export const { useGetExecutionLogsQuery, useGetExecutionLogFilterOptionsQuery } = executionLogsApi;
